import { Options } from '@xmpp/client';
import { xml, Client } from '@xmpp/client-core';
import debug from '@xmpp/debug';
import _iqCallee from '@xmpp/iq/callee';
import _iqCaller from '@xmpp/iq/caller';
import parseJid from '@xmpp/jid';
import _middleware from '@xmpp/middleware';
import _reconnect from '@xmpp/reconnect';
import _resolve from '@xmpp/resolve';
import _resourceBinding from '@xmpp/resource-binding';
import _sasl from '@xmpp/sasl';
import plain from '@xmpp/sasl-plain';
import _sessionEstablishment from '@xmpp/session-establishment';
import _streamFeatures from '@xmpp/stream-features';
import _websocket from '@xmpp/websocket';
import { v1 as uuidv1 } from 'uuid';

import { hmacSha256 } from '@/api/crypto';
import { Session } from 'src/api/types/Session';

// TODO: This is a custom version of xmpp.js' basic client without stream management.
//       It also removes anonymous authentication since we don't use it.
// - Reference: https://github.com/xmppjs/xmpp.js/discussions/941
// - Original: https://github.com/xmppjs/xmpp.js/blob/main/packages/client/browser.js
//
// The current stream management implementation employed in the XMPP server (Openfire)
// appears to cause duplicate message delivery when streams are closed in the following cases:
// - Send any direct chat message from web to iOS, then reload the page or logout.
// - Send text direct chat messages (via REST API) from web to web, reload the page or logout.
function client({
  resource,
  credentials,
  username,
  password,
  ...params
}: Options = {}) {
  const { domain, service } = params;
  if (!domain && service) {
    const server = new URL(service);
    params.domain = server.hostname;
  }

  const entity = new Client(params);

  const reconnect = _reconnect({ entity });
  const websocket = _websocket({ entity });

  const middleware = _middleware({ entity });
  const streamFeatures = _streamFeatures({ middleware });
  const iqCaller = _iqCaller({ middleware, entity });
  const iqCallee = _iqCallee({ middleware, entity });
  const resolve = _resolve({ entity });
  // Stream features - order matters and define priority
  const sasl = _sasl({ streamFeatures }, credentials || { username, password });
  const resourceBinding = _resourceBinding(
    { iqCaller, streamFeatures },
    resource,
  );
  const sessionEstablishment = _sessionEstablishment({
    iqCaller,
    streamFeatures,
  });
  // SASL mechanisms - order matters and define priority
  const mechanisms = Object.entries({ plain }).map(([k, v]) => ({
    [k]: v(sasl),
  }));

  return Object.assign(entity, {
    entity,
    reconnect,
    websocket,
    middleware,
    streamFeatures,
    iqCaller,
    iqCallee,
    resolve,
    sasl,
    resourceBinding,
    sessionEstablishment,
    mechanisms,
  });
}

function getResource(session: Pick<Session, 'id'>) {
  // The 'resource' needs to be unique enough.
  // This allows you to be connected to XMPP in more than one device with the same account.
  return `join_web_${session.id}_${uuidv1()}`;
}

function getCredentialsFn(session: Pick<Session, 'id' | 'jidNode' | 'token'>) {
  // https://github.com/xmppjs/xmpp.js/tree/main/packages/sasl#function
  return async (auth: (credentials: { username: string, password: string }) => Promise<void>) => {
    // Credentials are time-based, so this needs to run on every authentication attempt
    // https://git.allm.net/medicalline/join-backend/join-plugins/-/blob/master/docs/session-secret-login.md
    const date = Date.now() / 1000;
    const hmac = await hmacSha256(session.token, date.toString());

    await auth({
      username: session.jidNode,
      password: `${session.id}:${date}:${hmac}`,
    });
  };
}

export function makeXmppClient(url: string, session: Pick<Session, 'id' | 'jidNode' | 'token'>) {
  const xmpp = client({
    service: url,
    resource: getResource(session),
    credentials: getCredentialsFn(session),
  });

  if (process.env.ENV !== 'production') {
    // TODO: Evaluate having a less verbose debugging strategy
    debug(xmpp, true);
  }

  xmpp.on('online', async () => {
    // Enable carbons
    // https://git.allm.net/medicalline/join-backend/join-plugins/-/blob/master/docs/xmpp-bounced-carbon.md
    await xmpp.iqCaller.set(xml('enable', 'urn:xmpp:carbons:2'));

    // Notify that we are available. You cannot receive/send messages without doing this first.
    await xmpp.send(xml('presence'));
  });

  xmpp.on('error', () => {
    // TODO: Log errors for now. Evaluate if these can be handled or ignored safely.
  });

  return xmpp;
}

export function getJidNode(jid: string) {
  return parseJid(jid).getLocal();
}

export function getBareJid(jid: string) {
  return parseJid(jid).bare().toString();
}
